/**
 * 节点注册表
 * 允许节点组件自己注册，添加新节点类型时无需修改中心配置
 */

import type { ComponentType } from 'react'
import type { NodeProps } from '@xyflow/react'
import type { SerializableValue } from '@/shared/lib/serializable'
import {
  getFileExtension,
  matchesMimeType,
  normalizeResourceType,
} from '@/shared/lib/utils/resource-match-utils.ts'

export type { SerializableValue }

export type NodeContextKind = string

/**
 * 节点基础数据接口 (Shared 层定义)
 * 用于在注册表级别约束最基本的数据结构，避免 shared 依赖 entities
 */
export interface BaseNodeData {
  [key: string]: unknown
  title: string
  toolbar?: string
  op?: string
  op_data?: Record<string, unknown>
  error?: string,
  code?: number,
  taskId?: string,
  progress?: number,
  status?: 'pending' | 'running' | 'completed' | 'failed' | 'canceled',
  runId?: string // 任务创建关联的 id，用于取消和重试任务
}

/** 生成失败的素材引用 */
export interface ListNodeFailedRef {
  /** 素材 ID，用于查找显示图片；为空时只显示占位 */
  materialId: string
  /** 失败原因 / 错误信息 */
  msg?: string
}

/** 列表节点分页：一次批量上传或批量生成对应一页 */
export interface ListNodePage {
  /** 该页包含的 asset ID 列表（按插入顺序，越新越靠前） */
  assetRefs: string[]
  /** 该页生成失败的素材列表，依次显示在页面末尾 */
  failedRefs: ListNodeFailedRef[]
  /** op === grid-split 专用字段，记录该页具体的宫格密度 */
  cell?: string
  /** op === grid-split 专用字段，记录该页的第一张图片的宽高比 */
  gridSplitAspectRatio?: number
}

export interface NodeCreateAssetInput {
  type: string
  title: string
  width?: number
  height?: number
  originalUrl?: string
  previewUrl?: string
  duration?: number
  material_id?: string
  id?: string
  text?: string
  teamId?: string | number
}

export type NodeResourceCreateSource =
  {
    kind: 'uploaded-file'
    file: File
    uploadedUrl: string
    width: number
    height: number
    extra?: Record<string, unknown>
  } | {
    kind: 'asset'
    asset: NodeCreateAssetInput
  }

/**
 * 从创建源中获取资源 URL
 */
export function getNodeResourceUrl(source: NodeResourceCreateSource): string {
  if (source.kind === 'uploaded-file') {
    return source.uploadedUrl
  }
  return source.asset.originalUrl || source.asset.previewUrl || ''
}

export interface NodeCreateCapability {
  fileAccepts?: string[]
  assetAccepts?: string[]
  extensions?: string[]
  maxSize?: number
  maxDuration?: number
}

// connection config removed

export interface NodeContextPayloadLike<TData extends Record<string, unknown> = Record<string, unknown>> {
  kind: NodeContextKind
  edgeId: string
  nodeId: string
  nodeType: string
  title: string
  data: TData
  resource?: NodeContextResource
}

export interface NodeContextResource {
  assetId: string
  type: string
  url?: string
  previewUrl?: string
  width?: number
  height?: number
  duration?: number
  check_status?: number
}

export interface GroupTaskExecutionContext {
  executableNodeIds: ReadonlySet<string>
  estimatedItemCounts?: ReadonlyMap<string, number>
}

export interface NodeUpdateDataInfo {
  nodeId: string
  source?: NodeResourceCreateSource
}

export interface NodeDisplaySize {
  width: number
  height: number
}

export interface NodeDefinition<TData extends BaseNodeData = BaseNodeData> {
  type: string
  label?: string
  description?: string
  /**
   * 互斥连线组 (Exclusive Input Group)
   * 
   * 配置了该组名的节点作为输入源连接到某个目标节点时：
   * 引擎会检查该目标节点现有的所有连线，若发现有属于同名互斥组的其他输入源，
   * 则要求两者的节点类型(`type`)必须完全一致，否则拒绝连线。
   * 
   * 例如：配置为 'list' 组，可确保下游目标节点只能接收统一类型（如全是图片列表）的列表输入，
   * 而不会混杂接入文本列表、视频列表等不同类型的节点。
   */
  exclusiveInputGroup?: string
  create?: NodeCreateCapability
  createData: (source?: NodeResourceCreateSource) => TData
  updateData?: (info: NodeUpdateDataInfo) => Partial<TData> | void
  getSize?: (data: TData) => NodeDisplaySize | undefined
  sizeDataPaths?: readonly string[]
  component: ComponentType<NodeProps>
}

export type NodeDefinitionConfig<TData extends BaseNodeData = BaseNodeData> =
  Omit<NodeDefinition<TData>, 'component'>

const definitionRegistry = new Map<string, NodeDefinition<BaseNodeData>>()

/**
 * 注册节点定义（高阶函数方式）
 */
export function registerNodeDefinition<TData extends BaseNodeData>(
  config: NodeDefinitionConfig<TData>,
) {
  return function (Component: ComponentType<NodeProps>): ComponentType<NodeProps> {
    if (definitionRegistry.has(config.type)) {
      console.warn(`[node-registry] 节点类型 "${config.type}" 重复注册，将被覆盖`)
    }
    definitionRegistry.set(config.type, {
      type: config.type,
      label: config.label,
      description: config.description,
      exclusiveInputGroup: config.exclusiveInputGroup,
      createData: config.createData as (source?: NodeResourceCreateSource) => BaseNodeData,
      updateData: config.updateData as NodeDefinition<BaseNodeData>['updateData'],
      getSize: config.getSize as NodeDefinition<BaseNodeData>['getSize'],
      sizeDataPaths: config.sizeDataPaths,
      component: Component,
      create: config.create,
    })
    return Component
  }
}

function canCreateFromFile(definition: NodeDefinition<BaseNodeData>, file: File) {
  const create = definition.create

  if (!create) {
    return false
  }

  const fileExtension = getFileExtension(file.name)

  // 如果节点定义了 extensions 白名单，且文件有扩展名，但不在白名单内，则直接拒绝 (如 .gif)
  if (create.extensions?.length && fileExtension) {
    if (!create.extensions.includes(fileExtension)) {
      return false
    }
  }

  if (file.type && create.fileAccepts?.length) {
    if (create.fileAccepts.some((accept) => matchesMimeType(file.type, accept))) {
      return true
    }
  }

  if (create.extensions?.length && fileExtension) {
    return create.extensions.includes(fileExtension)
  }

  return false
}

function canCreateFromAsset(definition: NodeDefinition<BaseNodeData>, asset: NodeCreateAssetInput) {
  const create = definition.create

  if (!create?.assetAccepts?.length) {
    return false
  }

  return create.assetAccepts.includes(normalizeResourceType(asset.type))
}

// validateConnectionConfig removed

export function getNodeDefinition(type: string): NodeDefinition<BaseNodeData> | undefined {
  return definitionRegistry.get(type)
}

export function getAllNodeDefinitions(): NodeDefinition<BaseNodeData>[] {
  return Array.from(definitionRegistry.values())
}

export function findNodeDefinitionByFile(file: File): NodeDefinition<BaseNodeData> | undefined {
  return getAllNodeDefinitions().find((definition) => canCreateFromFile(definition, file))
}

export function findNodeDefinitionByAsset(asset: NodeCreateAssetInput): NodeDefinition<BaseNodeData> | undefined {
  return getAllNodeDefinitions().find((definition) => canCreateFromAsset(definition, asset))
}

export function createNodeData(type: string): BaseNodeData {
  const def = getNodeDefinition(type)
  if (!def) {
    console.warn(`Node type "${type}" not registered`)
    return { title: '' }
  }
  return def.createData()
}

export function clearNodeDefinitions() {
  definitionRegistry.clear()
}
